/**
 * Powers the Action Needed bell.
 *
 * Computes three counts (failedOps, approvalOps, failedWorkflows) from
 * GraphQL/ES queries, then layers AppSync subscriptions on top of the
 * two ops queries so the bell reacts within seconds to backend
 * transitions instead of waiting on the poll.
 *
 * Subscriptions don't update the Apollo cache directly — each
 * updateQuery schedules a refetch() with a small delay (2-12s) so ES
 * has time to reindex the DynamoDB write before the refetch lands.
 *
 * Workflows are pure polling (10 min); no subscription wired yet.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { useQuery } from "@apollo/client/react"
import { useSelector } from "react-redux"
import esb from "elastic-builder"

import SEARCH_ORDER_OPERATIONS_COUNT from "graphql/queries/orderOperations/SearchOrderOperationsCount"
import SEARCH_ORDER_WORKFLOW_EXECUTIONS from "graphql/queries/orderWorkflows/SearchOrderWorkflowExecutions"
import ON_ORDER_OPERATION_FAILED from "graphql/subscriptions/orderOperations/OnOrderOperationFailed"
import ON_ORDER_OPERATION_CREATED from "graphql/subscriptions/orderOperations/OnOrderOperationCreated"
import ON_ORDER_OPERATION_COMPLETED from "graphql/subscriptions/orderOperations/OnOrderOperationCompleted"
import ON_ORDER_OPERATION_DELETED from "graphql/subscriptions/orderOperations/OnOrderOperationDeleted"
import {
  GetCustomStatesDocument,
  GetOrderNotificationSettingsDocument,
  SearchOrdersCustomStateCountsDocument
} from "lib/types/generated/graphql"
import { useConfig } from "configuration/useConfig"
import { failedWorkflowWindowStart } from "helpers/failedWorkflowWindow"
import { CustomStateNotificationCount, NotificationCounts } from "lib/types/notifications"
import { selectUserGroups } from "lib/store/services/auth/selectors"
import { Groups, PermissionGroups } from "routes/constants"

const POLL_INTERVAL_WITH_SUBSCRIPTION = 1800000
const POLL_INTERVAL_WITHOUT_SUBSCRIPTION = 600000
const CREATED_RECONCILE_DELAY = 5000
const FAILED_REFETCH_DELAY = 2000
const COMPLETED_REFETCH_DELAY = 2000
const DELETED_REFETCH_DELAY = 5000
const DELETED_REFETCH_BACKUP_DELAY = 12000
const HOUR_BUCKET_TICK_MS = 60000
const MS_PER_HOUR = 3600000

// Polls pause while the tab is hidden or the client is offline; the
// visibilitychange handler below refetches everything the moment the tab
// regains focus. Skipping while offline avoids pointless failing requests
// (and the RUM/Cognito noise they trigger) during connectivity loss.
const skipPollAttemptWhenInactive = () =>
  document.hidden || (typeof navigator !== "undefined" && navigator.onLine === false)

const OPERATION_TYPES = [
  "Delivery",
  "Cancellation",
  "Refund",
  "Release",
  "Compensation",
  "OrderLinesModification",
  "ShippingFeesModification",
  "AddressesModification"
]

const FAILED_OPS_QUERY = JSON.stringify(
  esb
    .boolQuery()
    .must(esb.existsQuery("failed"))
    .must(esb.termsQuery("type", OPERATION_TYPES))
    .mustNot(esb.existsQuery("completed"))
    .toJSON()
)

const APPROVAL_OPS_QUERY = JSON.stringify(
  esb
    .boolQuery()
    .must(esb.termsQuery("type", OPERATION_TYPES))
    .mustNot(esb.existsQuery("started"))
    .mustNot(esb.existsQuery("failed"))
    .mustNot(esb.existsQuery("completed"))
    .toJSON()
)

// Windowed on `failed`, not `created` — a workflow created weeks ago that failed today is still actionable.
const buildWorkflowQuery = () =>
  JSON.stringify(
    esb
      .boolQuery()
      .must(esb.rangeQuery("failed").gte(failedWorkflowWindowStart().toISOString()))
      .mustNot(esb.existsQuery("completed"))
      .toJSON()
  )

interface UseNotificationCountsResult extends NotificationCounts {
  totalCount: number
  loading: boolean
  subscriptionsHealthy: boolean
}

const useNotificationCounts = (): UseNotificationCountsResult => {
  const { config } = useConfig()
  const actionNeededEnabled = config.actionNeededEnabled
  const operationsEnabled = config.operationsEnabled
  const workflowsEnabled = config.workflowsEnabled
  const userGroups = useSelector(selectUserGroups)
  const hasOpsRole = userGroups.some((g) =>
    (PermissionGroups.actionNeeded.operations as readonly string[]).includes(g)
  )
  const hasWorkflowsRole = userGroups.some((g) =>
    (PermissionGroups.actionNeeded.workflows as readonly string[]).includes(g)
  )
  const hasCustomStatesRole = userGroups.some(
    (g) => g === Groups.CX_USER || g === Groups.CX_ADMIN
  )
  const opsEnabledForUser = actionNeededEnabled && operationsEnabled && hasOpsRole
  const workflowsEnabledForUser = actionNeededEnabled && workflowsEnabled && hasWorkflowsRole
  const customStatesEnabledForUser = actionNeededEnabled && hasCustomStatesRole

  const [subscriptionsHealthy, setSubscriptionsHealthy] = useState(false)

  const [hourBucket, setHourBucket] = useState(() => Math.floor(Date.now() / MS_PER_HOUR))
  useEffect(() => {
    const id = setInterval(
      () => setHourBucket(Math.floor(Date.now() / MS_PER_HOUR)),
      HOUR_BUCKET_TICK_MS
    )
    return () => clearInterval(id)
  }, [])

  const workflowQuery = useMemo(() => buildWorkflowQuery(), [hourBucket])
  const pendingCreated = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map())

  const makeOperationKey = useCallback(
    (event: { orderId: string; type: string }) => `${event.orderId}:${event.type}`,
    []
  )

  const {
    data: failedOpsData,
    loading: failedOpsLoading,
    refetch: refetchFailedOps,
    subscribeToMore: subscribeToFailedOps
  } = useQuery(SEARCH_ORDER_OPERATIONS_COUNT, {
    variables: {
      query: FAILED_OPS_QUERY,
      from: 0,
      size: 0,
      trackTotalHits: true
    },
    pollInterval: POLL_INTERVAL_WITH_SUBSCRIPTION,
    skipPollAttempt: skipPollAttemptWhenInactive,
    errorPolicy: "ignore",
    skip: !opsEnabledForUser
  })

  const {
    data: approvalOpsData,
    loading: approvalOpsLoading,
    refetch: refetchApprovalOps,
    subscribeToMore: subscribeToApprovalOps
  } = useQuery(SEARCH_ORDER_OPERATIONS_COUNT, {
    variables: {
      query: APPROVAL_OPS_QUERY,
      from: 0,
      size: 0,
      trackTotalHits: true
    },
    pollInterval: POLL_INTERVAL_WITH_SUBSCRIPTION,
    skipPollAttempt: skipPollAttemptWhenInactive,
    errorPolicy: "ignore",
    skip: !opsEnabledForUser
  })

  // subscribeToMore is called directly in render body (no useEffect wrap),
  // matching the production-working Adyen pattern (AdyenConfigTableRow.tsx).
  // The skip flag on the parent useQuery does the role gating — when the
  // user has no ops permission the query is skipped and the calls below
  // short-circuit because subscribeToMore from a skipped useQuery is a no-op.
  const setUnhealthy = useCallback(() => setSubscriptionsHealthy(false), [])
  const setHealthy = useCallback(() => setSubscriptionsHealthy(true), [])

  if (opsEnabledForUser) {
    subscribeToFailedOps({
      document: ON_ORDER_OPERATION_FAILED,
      variables: {},
      onError: setUnhealthy,
      updateQuery: (_prev, { subscriptionData }) => {
        const event = subscriptionData.data?.OnOrderOperationFailed
        if (!event) return
        setHealthy()
        const key = makeOperationKey(event)
        const pending = pendingCreated.current.get(key)
        if (pending) {
          clearTimeout(pending)
          pendingCreated.current.delete(key)
        }
        setTimeout(() => {
          refetchFailedOps()
          refetchApprovalOps()
        }, FAILED_REFETCH_DELAY)
      }
    })

    // CREATED → COMPLETED/FAILED dedup: a freshly-created operation might
    // transition out of the approval bucket within seconds (auto-approved,
    // immediately fails, etc.). Rather than refetch on Created and again
    // on Completed/Failed, we schedule the Created refetch on a delay and
    // park its timer in `pendingCreated` keyed by orderId:type. If
    // Completed or Failed arrives for that key first, they cancel the
    // timer — their own refetch picks up the new state.
    subscribeToApprovalOps({
      document: ON_ORDER_OPERATION_CREATED,
      variables: {},
      onError: setUnhealthy,
      updateQuery: (_prev, { subscriptionData }) => {
        const event = subscriptionData.data?.OnOrderOperationCreated
        if (!event) return
        setHealthy()
        const key = makeOperationKey(event)
        const timer = setTimeout(() => {
          pendingCreated.current.delete(key)
          refetchApprovalOps()
        }, CREATED_RECONCILE_DELAY)
        pendingCreated.current.set(key, timer)
      }
    })

    subscribeToApprovalOps({
      document: ON_ORDER_OPERATION_COMPLETED,
      variables: {},
      onError: setUnhealthy,
      updateQuery: (_prev, { subscriptionData }) => {
        const event = subscriptionData.data?.OnOrderOperationCompleted
        if (!event) return
        setHealthy()
        const key = makeOperationKey(event)
        const pending = pendingCreated.current.get(key)
        if (pending) {
          clearTimeout(pending)
          pendingCreated.current.delete(key)
        }
        setTimeout(() => {
          refetchApprovalOps()
          refetchFailedOps()
        }, COMPLETED_REFETCH_DELAY)
      }
    })

    // Drafts can be discarded before they leave the approval bucket — refetch
    // so every logged-in user's bell drops without waiting on the poll. The
    // DynamoDB stream → EventBridge path beats the search-index update by
    // several seconds, so we delay (matching CREATED) and also schedule a
    // backup refetch in case ES indexing lags even further. We also refetch
    // failedOps because a deleted operation could have been in either bucket.
    subscribeToApprovalOps({
      document: ON_ORDER_OPERATION_DELETED,
      variables: {},
      onError: setUnhealthy,
      updateQuery: (_prev, { subscriptionData }) => {
        const event = subscriptionData.data?.OnOrderOperationDeleted
        if (!event) return
        setHealthy()
        const key = makeOperationKey(event)
        const pending = pendingCreated.current.get(key)
        if (pending) {
          clearTimeout(pending)
          pendingCreated.current.delete(key)
        }
        const refetchBoth = () => {
          refetchApprovalOps()
          refetchFailedOps()
        }
        setTimeout(refetchBoth, DELETED_REFETCH_DELAY)
        setTimeout(refetchBoth, DELETED_REFETCH_BACKUP_DELAY)
      }
    })
  }

  // Reset healthy on role/config change (state can't be set during render).
  // Only the ops subscriptions feed the flag — workflows are poll-only.
  useEffect(() => {
    setSubscriptionsHealthy(opsEnabledForUser)
  }, [opsEnabledForUser])

  // Cleanup any pending reconcile timers on unmount.
  useEffect(() => {
    const timers = pendingCreated.current
    return () => {
      timers.forEach((timer) => clearTimeout(timer))
      timers.clear()
    }
  }, [])

  // Workflows are pure polling — no subscription. 10-minute cadence is the
  // accepted lag for failed-workflow count to settle. The ops queries above
  // get the live-subscription treatment because that signal is reliable on
  // the private bus today.
  const {
    data: failedWorkflowsData,
    loading: failedWorkflowsLoading,
    refetch: refetchFailedWorkflows
  } = useQuery(SEARCH_ORDER_WORKFLOW_EXECUTIONS, {
    variables: {
      query: workflowQuery,
      from: 0,
      size: 0,
      trackTotalHits: true
    },
    pollInterval: POLL_INTERVAL_WITHOUT_SUBSCRIPTION,
    skipPollAttempt: skipPollAttemptWhenInactive,
    errorPolicy: "ignore",
    skip: !workflowsEnabledForUser
  })

  // Only the count query polls. Settings and customStates are fetched once
  // and live in the Apollo cache: own-tab selection changes land via the
  // optimistic mutation write, other users' changes via the tab-focus
  // refetch below, and customStates updates via the Settings page sharing
  // the same query.
  const { data: settingsData, refetch: refetchSettings } = useQuery(
    GetOrderNotificationSettingsDocument,
    {
      errorPolicy: "ignore",
      skip: !customStatesEnabledForUser
    }
  )

  const { data: customStatesData } = useQuery(GetCustomStatesDocument, {
    errorPolicy: "ignore",
    skip: !customStatesEnabledForUser
  })

  const knownStates = useMemo(
    () => customStatesData?.Order?.customStates?.states ?? [],
    [customStatesData]
  )

  // Drop ids for states deleted after they were selected — the settings have
  // no backend cascade on state deletion.
  const selectedStateIds = useMemo(() => {
    const settingsIds = settingsData?.getOrderNotificationSettings?.customStateIds ?? []
    const knownIds = new Set(knownStates.map((state) => state?.id))
    return settingsIds.filter((id) => knownIds.has(id))
  }, [settingsData, knownStates])

  const customStateVariables = useMemo(() => {
    if (selectedStateIds.length === 0) return undefined
    // Only placed orders are actionable — a custom state on a cancelled or
    // finished order shouldn't surface in the bell.
    const query = JSON.stringify(
      esb
        .boolQuery()
        .must(esb.termsQuery("status.customStates.id", selectedStateIds))
        .must(esb.termsQuery("status.orderStates", ["PLACED"]))
        .toJSON()
    )
    // include() keeps multi-state orders from leaking unselected buckets.
    const aggsBuilder = esb
      .requestBodySearch()
      .agg(
        esb
          .termsAggregation("customStateCounts", "status.customStates.id")
          .include(selectedStateIds)
          .size(selectedStateIds.length)
      )
      .toJSON() as { aggs: Record<string, unknown> }
    return { query, aggs: JSON.stringify(aggsBuilder.aggs) }
  }, [selectedStateIds])

  const {
    data: customStateCountsData,
    loading: customStateCountsLoading,
    refetch: refetchCustomStateCounts
  } = useQuery(SearchOrdersCustomStateCountsDocument, {
    variables: {
      from: 0,
      size: 0,
      query: customStateVariables?.query,
      aggs: customStateVariables?.aggs
    },
    pollInterval: POLL_INTERVAL_WITHOUT_SUBSCRIPTION,
    skipPollAttempt: skipPollAttemptWhenInactive,
    errorPolicy: "ignore",
    skip: !customStatesEnabledForUser || !customStateVariables
  })

  const customStateCounts: CustomStateNotificationCount[] = useMemo(() => {
    if (selectedStateIds.length === 0) return []
    const aggregationsRaw = customStateCountsData?.searchOrders?.aggregations
    const aggregations =
      typeof aggregationsRaw === "string" ? JSON.parse(aggregationsRaw) : aggregationsRaw
    const buckets: Array<{ key: string; doc_count: number }> =
      aggregations?.customStateCounts?.buckets ?? []
    const countByStateId = new Map(buckets.map((bucket) => [bucket.key, bucket.doc_count]))
    const stateById = new Map(knownStates.map((state) => [state?.id, state]))
    return selectedStateIds.flatMap((stateId) => {
      const state = stateById.get(stateId)
      if (!state) return []
      return [
        {
          stateId,
          label: state.description || state.id,
          type: state.type,
          count: countByStateId.get(stateId) ?? 0
        }
      ]
    })
  }, [selectedStateIds, customStateCountsData, knownStates])

  // On tab focus, refetch all applicable counts so the user sees fresh
  // numbers without reloading — browser-suspended tabs may have missed
  // subscription events while throttled. Also marks ops subscriptions
  // healthy (the link usually keeps the WS alive across tab-hides; this
  // catches the case where it didn't and silently reconnected).
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState !== "visible") return
      // Don't refetch into the void while offline — the poll-skip guard
      // already pauses polling, and these requests would just fail.
      if (typeof navigator !== "undefined" && navigator.onLine === false) return
      if (opsEnabledForUser) {
        setSubscriptionsHealthy(true)
        refetchFailedOps()
        refetchApprovalOps()
      }
      if (workflowsEnabledForUser) {
        refetchFailedWorkflows()
      }
      if (customStatesEnabledForUser) {
        refetchSettings()
        refetchCustomStateCounts()
      }
    }
    document.addEventListener("visibilitychange", handleVisibilityChange)
    return () => document.removeEventListener("visibilitychange", handleVisibilityChange)
  }, [
    opsEnabledForUser,
    workflowsEnabledForUser,
    customStatesEnabledForUser,
    refetchFailedOps,
    refetchApprovalOps,
    refetchFailedWorkflows,
    refetchSettings,
    refetchCustomStateCounts
  ])

  const failedOps = failedOpsData?.searchOrderOperations?.total ?? 0
  const approvalOps = approvalOpsData?.searchOrderOperations?.total ?? 0
  const failedWorkflows = failedWorkflowsData?.searchOrderWorkflowExecutions?.total ?? 0
  const customStateTotal = customStateCounts.reduce((sum, { count }) => sum + count, 0)

  return {
    failedOps,
    approvalOps,
    failedWorkflows,
    customStateCounts,
    totalCount: failedOps + approvalOps + failedWorkflows + customStateTotal,
    loading:
      failedOpsLoading || approvalOpsLoading || failedWorkflowsLoading || customStateCountsLoading,
    subscriptionsHealthy
  }
}

export default useNotificationCounts
